import { useLayoutEffect, useState } from 'react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import type { Direction } from './tooltip.types';
import { SPACING } from './tooltip.types';

/**
 * Positioning hook for portal mode (using Floating UI)
 * Uses state-based element tracking for React 19 compatibility
 */
export function usePortalPositioning(
  show: boolean,
  direction: Direction,
  targetRef?: React.RefObject<HTMLElement | null>,
) {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  );

  const { refs, floatingStyles, x, y } = useFloating({
    open: show,
    placement: direction,
    strategy: 'fixed',
    middleware: [
      offset(SPACING.gap),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: referenceElement,
    },
  });

  // Sync targetRef to state (React 19 compatible)
  // Runs on every render to catch ref changes
  useLayoutEffect(() => {
    const element = targetRef?.current ?? null;
    // Only update state if element actually changed to avoid unnecessary re-renders
    setReferenceElement((prev) => (prev !== element ? element : prev));
  }); // No deps - must run every render to catch React 19 ref timing

  const isReady = Boolean(referenceElement);
  const isPositioned = x != null && y != null && isReady;

  return { refs, floatingStyles, isReady, isPositioned };
}
